import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Observable, of, Subject, take } from 'rxjs';
import {
  User,
  UserActions,
  UserActionsPaged,
  UserPaged,
  UserDealersPaged,
  UserDealers,
  UserMenus,
  UserMenusPaged,
} from '../models/user-model/user';
import { environment } from '../../../environments/environment';
import { GeneralDTO } from '../models/components-general/general-dto.model';
import { PagedFilter } from '../models/components-general/general-filter.model';
import { GeneralService } from './general.service';
import { BaseCatalog } from '../models/components-general/general-catalog.model';
import { AuthResponse } from '../models/auth-model/auth.model';

@Injectable({ providedIn: 'root' })
export class UserService {
  apiUrl: string = environment.apiURL;
  private logoutSubject = new Subject<void>();
  public readonly logout$ = this.logoutSubject.asObservable();
  constructor(
    private http: HttpClient,
    private router: Router,
    private generalService: GeneralService,
  ) {}

  getUserById(userId: number): Observable<User> {
    const url = this.apiUrl + '/User/' + userId;
    return this.http.get<User>(url);
  }

  outSession(userId: number): Observable<boolean> {
    return this.http.get<boolean>(this.apiUrl + '/ControlLoginUser/closesession/' + userId);
  }

  getCurrentUserId(): number {
    const token = localStorage.getItem('token');
    if (!token) return 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return Number.parseInt(payload.unique_name, 10);
    } catch {
      return 0;
    }
  }

  public upsertUser(user: User): Observable<GeneralDTO | null> {

    if (user.id) {
      return this.http.put<GeneralDTO>(this.apiUrl + '/User/', user).pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
    }
    user.userStatusId = 1; // Establecer el estatus a ACTIVO para nuevos usuarios
    return this.http.post<GeneralDTO>(this.apiUrl + '/User', user).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getUsers(body: {
    pageSize: string | number;
    page: string | number;
    sortActive: string | number;
    sortDirection: string | number;
    filter: string | null;
    UserTypeId?: number | null;
  }): Observable<UserPaged | null> {
    const params = this.generalService.buildParams(body);
    return this.http.get<UserPaged>(this.apiUrl + '/User/usersPaged', { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getActionsToUser(
    userId: number,
  ): Observable<{ description: string; id: number }[] | null> {
    const params = this.generalService.buildParams({ userId });
    return this.http
      .get<
        { description: string; id: number }[]
      >(this.apiUrl + '/ActionsPrivilege/GetActionsToUser', { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getUserActions(
    pagedFilter: PagedFilter,
    userId: number,
  ): Observable<UserActionsPaged | null> {
    const params = this.generalService.buildParams({ ...pagedFilter, userId });
    return this.http
      .get<UserActionsPaged>(this.apiUrl + '/ActionsPrivilege/GetPagedActions', { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public upsertUserActions(userActions: UserActions): Observable<GeneralDTO | null> {
    return this.http
      .post<GeneralDTO>(this.apiUrl + '/ActionsPrivilege/UpsertUserActions', userActions)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getDealersToUser(userId: number): Observable<BaseCatalog[] | null> {
    const params = this.generalService.buildParams({ userId });
    return this.http.get<BaseCatalog[]>(this.apiUrl + '/Dealer/GetDealersToUser', { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getDealersUsers(userId: number): Observable<UserDealersPaged | null> {
    const params = this.generalService.buildParams({ userId });
    return this.http
      .get<UserDealersPaged>(this.apiUrl + '/UserCatalog/GetUsersDealers', { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public upsertUserDealers(userDealers: UserDealers): Observable<GeneralDTO | null> {
    return this.http
      .post<GeneralDTO>(this.apiUrl + '/UserCatalog/UpsertUserDealers', userDealers)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getMenusToUser(userId: number): Observable<UserMenus[] | null> {
    const params = this.generalService.buildParams({ userId });
    return this.http.get<UserMenus[]>(this.apiUrl + '/Menu', { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }
  public getUserMenus(pagedFilter: PagedFilter, userId: number): Observable<UserMenusPaged | null> {
    const params = this.generalService.buildParams({ ...pagedFilter, userId });
    return this.http
      .get<UserMenusPaged>(this.apiUrl + `/UserMenu/${userId}/assignedMenus`, { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }
  public upsertUserMenus(userMenus: UserMenus): Observable<GeneralDTO | null> {
    userMenus.parentDescription = userMenus.parentDescription ?? '';
    userMenus.description = userMenus.description ?? '';
    if (userMenus.status !== 'BORRADO') {
      return this.http.post<GeneralDTO>(this.apiUrl + '/UserMenu/assign', [userMenus]).pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
    }
    return this.http.post<GeneralDTO>(this.apiUrl + '/UserMenu/unassign', [userMenus]).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }
  public recoverPassword(body: {
    username: string;
    isWeb: boolean;
  }): Observable<GeneralDTO | null> {
    return this.http.post<GeneralDTO>(this.apiUrl + `/RecoveryPassword/SendMail`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }
  public changePassword(body: {
    userId: number | null;
    newPassword: string;
    isWeb: boolean;
  }): Observable<GeneralDTO | null> {
    return this.http.post<GeneralDTO>(this.apiUrl + `/RecoveryPassword/ChangePassword`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public validateUserToken(token: string): Observable<{ userId: number } | null> {
    return this.http
      .post<{ userId: number }>(this.apiUrl + `/RecoveryPassword/userToken`, { token })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }
}
